import { useRef, useLayoutEffect } from 'react';

/**
 * Спільні константи та хелпери для ProductImageGalleryMobile та ProductImageGalleryDesktop.
 */

export const MIN_ZOOM = 1;
export const MIN_DRAG_PX = 5;

/** Хук: скрол горизонтального контейнера перетягуванням миші (натиснув — тягнеш — скрол рухається), як свайп на телефоні. Повертає ref: якщо було перетягування — true, щоб не спрацьовував клік по мініатюрі. mountKey — якщо передано, ефект перепідпишеться при зміні (наприклад lightboxOpen). */
export const useDragToScroll = (containerRef, mountKey) => {
  const didDragRef = useRef(false);
  const runEffect = () => {
    const el = containerRef?.current;
    if (!el) return;
    let startX = 0;
    let startScrollLeft = 0;
    let moved = false;
    let active = false;
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      active = true;
      moved = false;
      didDragRef.current = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };
    const onMouseMove = (e) => {
      if (!active) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > MIN_DRAG_PX) moved = true;
      el.scrollLeft = startScrollLeft + startX - e.clientX;
    };
    const onMouseUp = () => {
      active = false;
      if (moved) didDragRef.current = true;
      el.style.cursor = '';
      el.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    el.addEventListener('mousedown', onMouseDown);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  };
  useLayoutEffect(() => {
    return runEffect();
  }, [containerRef, mountKey]);
  return didDragRef;
};

export const MAX_ZOOM = 4;
export const DOUBLE_TAP_MS = 300;
export const DOUBLE_TAP_MAX_DIST = 30;
export const MIN_SWIPE_PX = 50;
export const LIGHTBOX_MAX_WIDTH = 1920;
export const LIGHTBOX_MAX_WIDTH_CAP = 3840; /* макс. ширина для Retina/4K */
/** Для lightbox: ширина під екран і щільність пікселів, щоб зображення було чітким на Retina. */
export const getLightboxMaxWidth = () => {
  if (typeof window === 'undefined') return LIGHTBOX_MAX_WIDTH;
  const w = window.innerWidth;
  const dpr = window.devicePixelRatio || 1;
  return Math.min(LIGHTBOX_MAX_WIDTH_CAP, Math.max(LIGHTBOX_MAX_WIDTH, Math.round(w * dpr)));
};
export const THUMB_MAX_WIDTH = 240;

export const appendImageSizeParams = (url, { maxWidth, maxHeight }) => {
  if (!url || (maxWidth == null && maxHeight == null)) return url;
  const sep = url.includes('?') ? '&' : '?';
  const q = new URLSearchParams();
  if (maxWidth != null) q.set('maxWidth', String(maxWidth));
  if (maxHeight != null) q.set('maxHeight', String(maxHeight));
  return url + sep + q.toString();
};

export const getTouchDistance = (touches) => {
  if (!touches || touches.length < 2) return 0;
  const a = touches[0];
  const b = touches[1];
  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
};

export const normalizeGalleryItems = (items = []) =>
  items.map(it =>
    typeof it === 'string'
      ? { original: it, thumbnail: it }
      : { original: it.original, thumbnail: it.thumbnail || it.original }
  );

/**
 * Центрує обрану мініатюру в горизонтальному слайдері.
 * @param {HTMLElement | null} containerEl — контейнер з overflow-x: auto (ref.current)
 * @param {number} activeIndex — індекс обраної мініатюри
 * @param {{ lightbox: boolean }} opts — lightbox: true якщо структура lightbox (контейнер > inner > кнопки)
 */
export const scrollThumbIntoView = (containerEl, activeIndex, opts = {}) => {
  if (!containerEl || activeIndex < 0) return;
  const thumbs = opts.lightbox
    ? containerEl.firstElementChild?.children
    : containerEl.children;
  if (!thumbs || activeIndex >= thumbs.length) return;
  const thumb = thumbs[activeIndex];
  if (!thumb) return;
  const left = thumb.offsetLeft;
  const thumbWidth = thumb.offsetWidth;
  const scrollLeft = left + thumbWidth / 2 - containerEl.clientWidth / 2;
  const maxScroll = containerEl.scrollWidth - containerEl.clientWidth;
  const target = Math.max(0, Math.min(maxScroll, scrollLeft));
  containerEl.scrollTo({ left: target, behavior: 'smooth' });
};
